import { Command } from 'commander';
import ora, { type Ora } from 'ora';
import { createServices, destroyServices } from '../../services/index.js';
import { JobService } from '../../services/job.service.js';
import { validateStoreModelCompatibility } from '../../utils/model-validation.js';
import { spawnBackgroundWorker } from '../../workers/spawn-worker.js';
import type { GlobalOptions } from '../program.js';

export function createIndexCommand(getOptions: () => GlobalOptions): Command {
  const index = new Command('index')
    .description('Scan store files, chunk text, generate embeddings, save to LanceDB')
    .argument('<store>', 'Store ID or name')
    .option('--force', 'Re-index all files even if unchanged')
    .action(async (storeIdOrName: string, options: { force?: boolean }) => {
      const globalOpts = getOptions();
      const services = await createServices(
        globalOpts.config,
        globalOpts.dataDir,
        globalOpts.projectRoot
      );
      let exitCode = 0;
      try {
        indexLogic: {
          const store = await services.store.getByIdOrName(storeIdOrName);

          if (store === undefined) {
            console.error(`Error: Store not found: ${storeIdOrName}`);
            exitCode = 3;

            break indexLogic;
          }

          // Web stores need to be re-crawled, not re-indexed
          // Route to background crawl job
          if (store.type === 'web') {
            const dataDir = services.config.resolveDataDir();
            const jobService = new JobService(dataDir);
            const job = jobService.createJob({
              type: 'crawl',
              details: {
                storeName: store.name,
                storeId: store.id,
                url: store.url,
                maxPages: store.maxPages,
                crawlInstruction: store.crawlInstructions,
                extractInstruction: store.extractInstructions,
                phase: 'crawling',
                phaseStep: 1,
                phaseTotalSteps: 2,
              },
              message: `Re-crawling ${store.name}...`,
            });
            spawnBackgroundWorker(job.id, dataDir);

            if (globalOpts.format === 'json') {
              console.log(
                JSON.stringify(
                  {
                    store: { id: store.id, name: store.name, type: store.type },
                    job: { id: job.id, status: job.status },
                    message: `Re-crawl queued (Job ID: ${job.id})`,
                  },
                  null,
                  2
                )
              );
            } else if (globalOpts.quiet !== true) {
              console.log(`Re-crawl job queued for ${store.name} (Job ID: ${job.id})`);
              console.log(`Check status: bluera-knowledge jobs status ${job.id}`);
            }

            break indexLogic;
          }

          // Use spinner in interactive mode (not quiet, not json output)
          const isInteractive =
            process.stdout.isTTY && globalOpts.quiet !== true && globalOpts.format !== 'json';
          let spinner: Ora | undefined;

          if (isInteractive) {
            spinner = ora(`Indexing store: ${store.name}`).start();
          } else if (globalOpts.quiet !== true && globalOpts.format !== 'json') {
            console.log(`Indexing store: ${store.name}`);
          }

          services.lance.setDimensions(await services.embeddings.ensureDimensions());
          await services.lance.initialize(store.id);

          // Validate model compatibility for incremental indexing (not --force)
          // Full reindex is always allowed since it replaces all data
          if (options.force !== true) {
            const currentModelId = services.store.getCurrentModelId();
            validateStoreModelCompatibility(store, { currentModelId });
          }

          // Use full re-index with --force, otherwise incremental (default)
          const progressCallback = (event: {
            type: string;
            current: number;
            total: number;
            message: string;
          }): void => {
            if (event.type === 'progress') {
              if (spinner) {
                spinner.text = `Indexing: ${String(event.current)}/${String(event.total)} files - ${event.message}`;
              }
            }
          };

          const result =
            options.force === true
              ? await services.index.indexStore(store, progressCallback)
              : await services.index.indexStoreIncremental(store, progressCallback);

          if (result.success) {
            // Upgrade schema v1 stores to v2 after successful reindex
            // This adds modelId so the store becomes searchable
            if (!store.modelId) {
              await services.store.upgradeStoreSchema(store.id);
            }

            if (globalOpts.format === 'json') {
              console.log(JSON.stringify(result.data, null, 2));
            } else {
              const message = `Indexed ${String(result.data.filesIndexed)} documents, ${String(result.data.chunksCreated)} chunks in ${String(result.data.timeMs)}ms`;
              if (spinner !== undefined) {
                spinner.succeed(message);
              } else if (globalOpts.quiet !== true) {
                console.log(message);
              }
            }
          } else {
            const message = `Error: ${result.error.message}`;
            if (spinner !== undefined) {
              spinner.fail(message);
            } else {
              console.error(message);
            }
            exitCode = 4;

            break indexLogic;
          }
        }
      } finally {
        await destroyServices(services);
      }

      if (exitCode !== 0) {
        // Using process.exitCode avoids mutex crashes from native code (LanceDB, tree-sitter)
        process.exitCode = exitCode;
      }
    });

  index
    .command('watch <store>')
    .description('Watch store directory; re-index when files change')
    .option(
      '--debounce <ms>',
      'Wait N ms after last change before re-indexing (default: 1000)',
      '1000'
    )
    .action(async (storeIdOrName: string, options: { debounce: string }) => {
      const globalOpts = getOptions();
      const services = await createServices(
        globalOpts.config,
        globalOpts.dataDir,
        globalOpts.projectRoot
      );

      const store = await services.store.getByIdOrName(storeIdOrName);
      if (store === undefined || (store.type !== 'file' && store.type !== 'repo')) {
        console.error(`Error: File/repo store not found: ${storeIdOrName}`);
        // Cleanup services before exiting (using exitCode avoids mutex crashes)
        await destroyServices(services);
        process.exitCode = 3;
        return;
      }

      const appConfig = await services.config.load();
      const { WatchService } = await import('../../services/watch.service.js');
      const watchService = new WatchService(services.index, services.lance, services.embeddings, {
        ignorePatterns: appConfig.indexing.ignorePatterns,
        currentModelId: services.store.getCurrentModelId(),
      });

      const debounceMs = parseInt(options.debounce, 10);
      if (Number.isNaN(debounceMs)) {
        throw new Error(
          `Invalid value for --debounce: "${options.debounce}" is not a valid integer`
        );
      }

      if (globalOpts.quiet !== true) {
        console.log(`Watching ${store.name} for changes...`);
      }
      await watchService.watch(
        store,
        debounceMs,
        () => {
          if (globalOpts.quiet !== true) {
            console.log(`Re-indexed ${store.name}`);
          }
        },
        (error: Error) => {
          console.error(`Watch error: ${error.message}`);
        }
      );

      // Keep process alive
      process.on('SIGINT', () => {
        void (async (): Promise<void> => {
          await watchService.unwatchAll();
          await destroyServices(services);
          process.exit(0);
        })().catch((err: unknown) => {
          console.error('Shutdown error:', err);
          process.exit(1);
        });
      });
    });

  return index;
}
